import React from 'react';
import { StatusDot } from '../components/ui';
import { isModeLocked, type LockedModes } from './modes';
import type { TitleSearchRailStep } from './titleSearchStatus';
import type { WorkspaceMode } from './urlState';

/**
 * StepList: the TR steps with status dots, below the OrderRail. Each step
 * opens the Mode it belongs to. Steps whose Mode is locked are disabled with
 * the reason in `title`. The status is always written out beside the dot.
 *
 * Requirement: 3.1, 3.5.
 */
export interface StepListProps {
  steps: readonly TitleSearchRailStep[];
  /** Mode currently shown; the matching step is marked current. */
  activeMode: WorkspaceMode;
  locked?: LockedModes;
  onSelectMode: (mode: WorkspaceMode) => void;
  className?: string;
}

export const StepList: React.FC<StepListProps> = ({
  steps,
  activeMode,
  locked,
  onSelectMode,
  className = '',
}) => (
  <nav aria-label="Title search steps" className={`p-4 ${className}`.trim()}>
    <h2 className="mb-2 text-[11px] font-bold uppercase tracking-[0.1em] text-gray-500">Steps</h2>
    <ol className="space-y-1">
      {steps.map((step) => {
        const isLocked = isModeLocked(locked, step.mode);
        const isCurrent = step.mode === activeMode && !isLocked;
        const reason = isLocked ? locked?.[step.mode] || 'Not available yet' : undefined;
        return (
          <li key={step.key}>
            <button
              type="button"
              data-step={step.key}
              disabled={isLocked}
              title={reason}
              aria-current={isCurrent ? 'step' : undefined}
              onClick={() => {
                if (isLocked) return;
                onSelectMode(step.mode);
              }}
              className={`flex w-full items-center gap-3 rounded-lg border-l-[3px] px-3 py-2 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#834DFB] ${
                isCurrent ? 'border-[#834DFB] bg-[#F5F3FF]' : 'border-transparent'
              } ${isLocked ? 'cursor-not-allowed' : 'hover:bg-gray-50'}`}
            >
              <StatusDot tone={step.tone} size={10} />
              <span className="min-w-0 flex-1">
                <span
                  className={`block text-sm font-bold ${isLocked ? 'text-[#b4adc4]' : 'text-gray-900'}`}
                >
                  {step.label}
                </span>
                <span className="block text-xs text-gray-500">
                  {isLocked ? 'Locked' : step.statusLabel}
                </span>
              </span>
            </button>
          </li>
        );
      })}
    </ol>
  </nav>
);

export default StepList;
